'use client'

import { type FC, useEffect, useState } from 'react'

import { signOut } from 'aws-amplify/auth'

import { APP_ENV, APP_ENVS } from '@/constants/env'
import { routes } from '@/constants/routes'
import { isAuthenticationError } from '@/lib/errors'
import { clearAllCookies } from '@/utils/cookies'

type GlobalErrorProps = {
  readonly error: Error & { digest?: string }
  readonly reset: () => void
}

const GlobalError: FC<GlobalErrorProps> = (props) => {
  const { error, reset } = props
  const [showDetails, setShowDetails] = useState(false)

  const isProduction = APP_ENV === APP_ENVS.PRODUCTION
  const isAuthError = isAuthenticationError(error)

  // Detect hydration errors
  const isHydrationError =
    error.message.includes('Hydration') ||
    error.message.includes('hydrat') ||
    error.message.includes('Text content does not match') ||
    error.message.includes('did not match') ||
    error.message.includes('Minified React error')

  const handleLoginRedirect = async () => {
    await signOut()
    globalThis.location.href = routes.login
  }

  const copyErrorDetails = async () => {
    const details = {
      message: error.message,
      name: error.name,
      stack: error.stack,
      digest: error.digest,
      url: globalThis.location?.href,
      userAgent: globalThis.navigator?.userAgent,
      timestamp: new Date().toISOString(),
    }
    await navigator.clipboard.writeText(JSON.stringify(details, null, 2))
  }

  useEffect(() => {
    if (isAuthError) {
      clearAllCookies()
      globalThis.location.href = routes.login
      return
    }

    // Also log to console for immediate visibility
    console.error('[GlobalError] Captured exception:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      digest: error.digest,
    })
  }, [error, isHydrationError, isAuthError])

  return (
    <html lang='en'>
      <head>
        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <title>Error - Shalion Dashboard</title>
        <style>{`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            color: #333;
          }

          .error-container {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            max-width: 600px;
            width: 100%;
            padding: 48px;
            text-align: center;
          }

          .error-icon {
            width: 80px;
            height: 80px;
            background: ${isAuthError ? '#FEF3C7' : '#FEE2E2'};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 24px;
            font-size: 40px;
          }

          h1 {
            font-size: 28px;
            font-weight: 700;
            color: #1a202c;
            margin-bottom: 12px;
          }

          .error-type {
            display: inline-block;
            background: ${isAuthError ? '#FEF3C7' : '#FEE2E2'};
            color: ${isAuthError ? '#92400E' : '#991B1B'};
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 13px;
            font-weight: 600;
            margin-bottom: 20px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .error-message {
            color: #4a5568;
            font-size: 16px;
            line-height: 1.6;
            margin-bottom: 32px;
            padding: 16px;
            background: #f7fafc;
            border-radius: 8px;
            border-left: 4px solid ${isAuthError ? '#F59E0B' : '#EF4444'};
          }

          .error-details {
            font-size: 14px;
            color: #718096;
            margin-bottom: 32px;
          }

          .button-group {
            display: flex;
            gap: 12px;
            justify-content: center;
            flex-wrap: wrap;
          }

          button, a {
            padding: 14px 28px;
            border-radius: 8px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            text-decoration: none;
            display: inline-block;
            transition: all 0.2s ease;
            border: none;
          }

          .primary-button {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }

          .primary-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 16px rgba(102, 126, 234, 0.4);
          }

          .secondary-button {
            background: #e2e8f0;
            color: #4a5568;
          }

          .secondary-button:hover {
            background: #cbd5e0;
          }

          .digest {
            margin-top: 24px;
            padding-top: 24px;
            border-top: 1px solid #e2e8f0;
            font-size: 12px;
            color: #a0aec0;
          }

          @media (max-width: 640px) {
            .error-container {
              padding: 32px 24px;
            }

            h1 {
              font-size: 24px;
            }

            .button-group {
              flex-direction: column;
            }

            button, a {
              width: 100%;
            }
          }
        `}</style>
      </head>
      <body>
        <div className='error-container'>
          <div className='error-icon'>{isAuthError ? '🔒' : '⚠️'}</div>

          <h1>{isAuthError ? 'Authentication Required' : 'Something Went Wrong'}</h1>

          <span className='error-type'>
            {isAuthError ? 'Authentication Error' : 'Application Error'}
          </span>

          <div className='error-message'>
            {isAuthError
              ? 'Your session has expired or is invalid. Please log in again to continue using the dashboard.'
              : 'An unexpected error occurred while processing your request. Our team has been notified and is working to resolve the issue.'}
          </div>

          {!isAuthError && error.message && (
            <div className='error-details'>
              <strong>Error details:</strong> {error.message}
            </div>
          )}

          {isHydrationError && (
            <div className='error-details' style={{ borderLeftColor: '#F59E0B' }}>
              <strong>⚠️ Hydration Error Detected:</strong> This error occurs when the
              server-rendered HTML does not match what React expects on the client. Check
              the browser console for more details.
            </div>
          )}

          <div className='button-group'>
            {isAuthError ? (
              <button
                type='button'
                onClick={handleLoginRedirect}
                className='primary-button'
              >
                Go to Login
              </button>
            ) : (
              <>
                <button type='button' onClick={() => reset()} className='primary-button'>
                  Try Again
                </button>
                <a href={routes.index} className='secondary-button'>
                  Go to Home
                </a>
              </>
            )}
          </div>

          {/* Debug panel for non-production */}
          {!isProduction && (
            <div style={{ marginTop: '24px', textAlign: 'left' }}>
              <button
                type='button'
                onClick={() => setShowDetails(!showDetails)}
                style={{
                  background: 'none',
                  border: '1px solid #e2e8f0',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#4a5568',
                  marginBottom: '12px',
                }}
              >
                {showDetails ? '▼ Hide' : '▶ Show'} Debug Details
              </button>

              {showDetails && (
                <div
                  style={{
                    background: '#1a202c',
                    color: '#e2e8f0',
                    padding: '16px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#f56565' }}>Name:</strong> {error.name}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#f56565' }}>Message:</strong> {error.message}
                  </div>
                  {error.digest && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#f56565' }}>Digest:</strong> {error.digest}
                    </div>
                  )}
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#f56565' }}>Stack Trace:</strong>
                    <pre
                      style={{
                        marginTop: '4px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {error.stack}
                    </pre>
                  </div>
                  <button
                    type='button'
                    onClick={copyErrorDetails}
                    style={{
                      background: '#4a5568',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      marginTop: '8px',
                    }}
                  >
                    📋 Copy Error Details
                  </button>
                </div>
              )}
            </div>
          )}

          {error.digest && <div className='digest'>Error ID: {error.digest}</div>}
        </div>
      </body>
    </html>
  )
}

export default GlobalError
